// File: e1c/src/components/ReadOnlySurveyRenderer.js
// MODIFIED: Added render cases for QUIZ_QUESTION_CHOICE and QUIZ_QUESTION_OPEN_TEXT
// MODIFIED (v2): Added FeedbackBox component and logic to display feedback/model answers.

import React, { useState } from 'react';
import { format, parseISO } from 'date-fns';
import {
  Box, Typography, Paper, Alert,
  RadioGroup, Radio, FormControlLabel, FormControl, FormLabel, Slider, TextField, Checkbox, FormGroup,
  Tooltip, List, ListItem, ListItemText, Divider, Chip, Button, Grid, Card, alpha
} from '@mui/material';

import { BLOCK_TYPES } from 'constants/contentBlockTypes';

import CircleIcon from '@mui/icons-material/Circle';
import LinkIcon from '@mui/icons-material/Link';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'; // For Yes
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked'; // For No or unselected
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'; // For Not Answered
// --- START OF ADDITION (v2) ---
import LightbulbIcon from '@mui/icons-material/Lightbulb'; // For Feedback
// --- END OF ADDITION (v2) ---

// Helper function to parse options (from original file)
const parseOptions = (optionsData) => {
  if (typeof optionsData === 'object' && optionsData !== null) {
    return optionsData;
  }
  if (typeof optionsData === 'string') {
    try {
      return JSON.parse(optionsData);
    } catch (e) {
      console.error("ReadOnlySurveyRenderer: Failed to parse block.options JSON string:", optionsData, e);
      return {};
    }
  }
  return {};
};

// Helper function to get slider marks (from original file)
const getSliderMarks = (options) => {
    const safeOptions = (typeof options === 'object' && options !== null) ? options : {};
    const min = parseFloat(safeOptions?.painScaleMin !== undefined ? safeOptions.painScaleMin : safeOptions?.min);
    const max = parseFloat(safeOptions?.painScaleMax !== undefined ? safeOptions.painScaleMax : safeOptions?.max);
    const step = (typeof safeOptions?.painScaleStep === 'number' && safeOptions.painScaleStep > 0) ? safeOptions.painScaleStep : (typeof safeOptions?.step === 'number' && safeOptions.step > 0 ? safeOptions.step : 1);

    const minLabel = safeOptions?.minLabel || (Number.isFinite(min) ? min.toString() : 'Min');
    const maxLabel = safeOptions?.maxLabel || (Number.isFinite(max) ? max.toString() : 'Max');

    if (!Number.isFinite(min) || !Number.isFinite(max) || min >= max) {
        // Fallback for invalid range, adjust as needed
        const defaultMin = 0;
        const defaultMax = 10;
        return [{ value: defaultMin, label: minLabel || String(defaultMin) }, { value: defaultMax, label: maxLabel || String(defaultMax) }];
    }
    const marks = [{ value: min, label: minLabel }, { value: max, label: maxLabel }];

    if (safeOptions?.showIntermediateMarks !== false && step > 0 && (max - min) / step <= 12 && (max - min) > 0) {
        for (let i = min + step; i < max; i += step) {
             if (i !== min && i !== max) marks.push({ value: parseFloat(i.toFixed(2)), label: parseFloat(i.toFixed(2)).toString() });
        }
    }
    marks.sort((a, b) => a.value - b.value);
    return marks;
};


// Enhanced helper function to render the response value in a more readable format
const renderFormattedResponseValue = (block, responseValue, blockOptions) => {
    if (responseValue === undefined || responseValue === null || responseValue === '') {
      return <Chip label="Not Answered" variant="outlined" size="small" icon={<HelpOutlineIcon />} sx={{ fontStyle: 'italic' }} />;
    }
  
    // Normalize the block type from the data for comparison with BLOCK_TYPES (which are lowercase)
    const normalizedBlockType = typeof block.type === 'string' ? block.type.toLowerCase() : 'unknown'; // Normalize here

    switch (normalizedBlockType) { // <<< USE THE NORMALIZED (lowercase) TYPE HERE
      case BLOCK_TYPES.YES_NO: // This is "yes_no"
        const isYes = String(responseValue).toLowerCase() === String(blockOptions.yesValue || "yes").toLowerCase(); // Add .toLowerCase() for robust comparison
        return <Chip 
                    label={isYes ? (blockOptions.yesLabel || 'Yes') : (blockOptions.noLabel || 'No')} 
                    icon={isYes ? <CheckCircleOutlineIcon /> : <RadioButtonUncheckedIcon />}
                    color={isYes ? 'success' : 'default'} 
                    variant="outlined"
                    size="small" 
                />;

      // --- START OF EDIT 1 ---
      // ADD QUIZ_QUESTION_CHOICE to these cases
      case BLOCK_TYPES.QUIZ_QUESTION_CHOICE:
      // --- END OF EDIT 1 ---
      case BLOCK_TYPES.SINGLE_CHOICE_SELECT: // This is "single_choice_select"
        const singleChoices = Array.isArray(blockOptions.choices) ? blockOptions.choices : [];
        const selectedSingleOption = singleChoices.find(opt => String(opt.value) === String(responseValue));
        const selectedSingleLabel = selectedSingleOption ? selectedSingleOption.label : String(responseValue); // Fallback to raw value if label not found
        
        // --- START OF ADDITION (v2) ---
        // Add color hint for quiz answers
        let color = "primary";
        if (normalizedBlockType === BLOCK_TYPES.QUIZ_QUESTION_CHOICE && selectedSingleOption) {
            color = selectedSingleOption.is_correct ? "success" : "error";
        }
        return <Chip label={selectedSingleLabel} size="small" variant="outlined" color={color} />;
        // --- END OF ADDITION (v2) ---
      
      case BLOCK_TYPES.MULTI_CHOICE_SELECT: // This is "multi_choice_select"
        // Note: This logic assumes QUIZ_QUESTION_CHOICE with isMultiSelect=true
        // will fall under MULTI_CHOICE_SELECT if we structure the renderBlockInput correctly.
        // Let's make this more robust for quizzes.
        const multiChoices = Array.isArray(blockOptions.choices) ? blockOptions.choices : [];
        const selectedValues = Array.isArray(responseValue) ? responseValue.map(String) 
                             : (responseValue ? [String(responseValue)] : []);
                             
        const selectedMultiLabels = selectedValues.map(val => {
            const foundOption = multiChoices.find(opt => String(opt.value) === val);
            return {
                label: foundOption ? foundOption.label : val, // Fallback to raw value
                is_correct: foundOption ? !!foundOption.is_correct : false // Check correctness
            };
        });
        
        if (selectedMultiLabels.length === 0) {
            return <Chip label="Not Answered" variant="outlined" size="small" icon={<HelpOutlineIcon />} sx={{ fontStyle: 'italic' }} />;
        }
        
        // --- START OF ADDITION (v2) ---
        // Add color hints for quiz answers
        return selectedMultiLabels.map(item => {
            let chipColor = "info";
            if (normalizedBlockType === BLOCK_TYPES.QUIZ_QUESTION_CHOICE) {
                 chipColor = item.is_correct ? "success" : "error";
            }
            return <Chip key={item.label} label={item.label} size="small" variant="filled" color={chipColor} sx={{ mr: 0.5, mb: 0.5 }} />;
        });
        // --- END OF ADDITION (v2) ---
      
      case BLOCK_TYPES.SCALE: // This is "scale"
      case BLOCK_TYPES.NUMBER: // This is "number"
        return <Chip label={String(responseValue) + (blockOptions.unit ? ` ${blockOptions.unit}` : '')} color="primary" variant="outlined" size="small" />;
      
      // --- START OF EDIT 2 ---
      // ADD QUIZ_QUESTION_OPEN_TEXT to this case
      case BLOCK_TYPES.QUIZ_QUESTION_OPEN_TEXT:
      // --- END OF EDIT 2 ---
      case BLOCK_TYPES.OPEN_TEXT: // This is "open_text"
        return <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontStyle: 'italic', p:1, border: '1px solid #eee', borderRadius: 1, background: '#f9f9f9' }}>{String(responseValue)}</Typography>;
      
      // PAIN_MAP is handled by its specific renderer in renderBlockInput
      
      default: 
        console.warn(`ReadOnlySurveyRenderer: Unhandled or unknown normalized block type "${normalizedBlockType}" in renderFormattedResponseValue. Original type: "${block.type}". Displaying raw response.`);
        return <Typography variant="body2">{String(responseValue)}</Typography>;
    }
  };

// ... (AssessmentResultBlock component remains unchanged) ...
const AssessmentResultBlock = ({ responseValue, questionOptions, block }) => {
    const [showDetails, setShowDetails] = useState(false);

    // BDI-II data sourced from your documentation. We need this here to look up the labels.
    const BDI_II_QUESTIONS = {
        'bdi_1': { text: 'Sadness', options: [{ score: 0, label: 'I do not feel sad.' }, { score: 1, label: 'I feel sad much of the time.' }, { score: 2, label: 'I am sad all the time.' }, { score: 3, label: 'I am so sad or unhappy that I can\'t stand it.' }] },
        'bdi_2': { text: 'Pessimism', options: [{ score: 0, label: 'I am not particularly pessimistic or discouraged about the future.' }, { score: 1, label: 'I feel more pessimistic about the future than I used to.' }, { score: 2, label: 'I do not expect things to work out for me.' }, { score: 3, label: 'I feel my future is hopeless and will only get worse.' }] },
        'bdi_3': { text: 'Past Failure', options: [{ score: 0, label: 'I do not feel like a failure.' }, { score: 1, label: 'I have failed more than I should have.' }, { score: 2, label: 'As I look back, I see a lot of failures.' }, { score: 3, label: 'I feel I am a total failure as a person.' }] },
        'bdi_4': { text: 'Loss of Pleasure', options: [{ score: 0, label: 'I get as much pleasure as I ever did from the things I enjoy.' }, { score: 1, label: 'I don\'t enjoy things as much as I used to.' }, { score: 2, label: 'I get very little pleasure from the things I used to enjoy.' }, { score: 3, label: 'I can\'t get any pleasure from the things I used to enjoy.' }] },
        'bdi_5': { text: 'Guilt Feelings', options: [{ score: 0, label: 'I don\'t feel particularly guilty.' }, { score: 1, label: 'I feel guilty over many things I have done or should have done.' }, { score: 2, label: 'I feel quite guilty most of the time.' }, { score: 3, label: 'I feel guilty all of the time.' }] },
        'bdi_6': { text: 'Punishment Feelings', options: [{ score: 0, label: 'I don\'t feel I am being punished.' }, { score: 1, label: 'I feel I may be punished.' }, { score: 2, label: 'I expect to be punished.' }, { score: 3, label: 'I feel I am being punished.' }] },
        'bdi_7': { text: 'Self-Dislike', options: [{ score: 0, label: 'I feel the same about myself as ever.' }, { score: 1, label: 'I have lost confidence in myself.' }, { score: 2, label: 'I am disappointed in myself.' }, { score: 3, label: 'I dislike myself.' }] },
        'bdi_8': { text: 'Self-Criticalness', options: [{ score: 0, label: 'I don\'t criticize or blame myself more than usual.' }, { score: 1, label: 'I am more critical of myself than I used to be.' }, { score: 2, label: 'I criticize myself for all of my faults.' }, { score: 3, label: 'I blame myself for everything bad that happens.' }] },
        'bdi_9': { text: 'Suicidal Thoughts or Wishes', options: [{ score: 0, label: 'I don\'t have any thoughts of killing myself.' }, { score: 1, label: 'I have thoughts of killing myself, but I would not carry them out.' }, { score: 2, label: 'I would like to kill myself.' }, { score: 3, label: 'I would kill myself if I had the chance.' }] },
        'bdi_10': { text: 'Crying', options: [{ score: 0, label: 'I don\'t cry anymore than I used to.' }, { score: 1, label: 'I cry more than I used to.' }, { score: 2, label: 'I cry over every little thing.' }, { score: 3, label: 'I feel like crying, but I can\'t.' }] },
        'bdi_11': { text: 'Agitation', options: [{ score: 0, label: 'I am no more restless or wound up than usual.' }, { score: 1, label: 'I feel more restless or wound up than usual.' }, { score: 2, label: 'I am so restless or agitated that it\'s hard to stay still.' }, { score: 3, label: 'I am so restless or agitated that I have to keep moving or doing something.' }] },
        'bdi_12': { text: 'Loss of Interest', options: [{ score: 0, label: 'I have not lost interest in other people or activities.' }, { score: 1, label: 'I am less interested in other people or things than before.' }, { score: 2, label: 'I have lost most of my interest in other people or things.' }, { score: 3, label: 'It\'s hard to get interested in anything.' }] },
        'bdi_13': { text: 'Indecisiveness', options: [{ score: 0, label: 'I make decisions about as well as ever.' }, { score: 1, label: 'I find it more difficult to make decisions than usual.' }, { score: 2, label: 'I have much greater difficulty in making decisions than I used to.' }, { score: 3, label: 'I have trouble making any decisions at all.' }] },
        'bdi_14': { text: 'Worthlessness', options: [{ score: 0, label: 'I do not feel I am worthless.' }, { score: 1, label: 'I don\'t consider myself as worthwhile and useful as I used to.' }, { score: 2, label: 'I feel more worthless as compared to other people.' }, { score: 3, label: 'I feel utterly worthless.' }] },
        'bdi_15': { text: 'Loss of Energy', options: [{ score: 0, label: 'I have as much energy as ever.' }, { score: 1, label: 'I have less energy than I used to have.' }, { score: 2, label: 'I don\'t have enough energy to do very much.' }, { score: 3, label: 'I don\'t have enough energy to do anything.' }] },
        'bdi_16': { text: 'Changes in Sleeping Pattern', options: [{ score: 0, label: 'I have not experienced any change in my sleeping pattern.' }, { score: 1, label: 'I sleep somewhat more or less than usual.' }, { score: 2, label: 'I sleep a lot more or less than usual.' }, { score: 3, label: 'I sleep most of the day or I wake up 1-2 hours early and can\'t get back to sleep.' }] },
        'bdi_17': { text: 'Irritability', options: [{ score: 0, label: 'I am no more irritable than usual.' }, { score: 1, label: 'I am more irritable than usual.' }, { score: 2, label: 'I am much more irritable than usual.' }, { score: 3, label: 'I am irritable all the time.' }] },
        'bdi_18': { text: 'Changes in Appetite', options: [{ score: 0, label: 'I have not experienced any change in my appetite.' }, { score: 1, label: 'My appetite is somewhat less or greater than usual.' }, { score: 2, label: 'My appetite is much less or greater than usual.' }, { score: 3, label: 'I have no appetite at all or I crave food all the time.' }] },
        'bdi_19': { text: 'Concentration Difficulty', options: [{ score: 0, label: 'I can concentrate as well as ever.' }, { score: 1, label: 'I can\'t concentrate as well as usual.' }, { score: 2, label: 'It\'s hard to keep my mind on anything for very long.' }, { score: 3, label: 'I find I can\'t concentrate on anything.' }] },
        'bdi_20': { text: 'Tiredness or Fatigue', options: [{ score: 0, label: 'I am no more tired or fatigued than usual.' }, { score: 1, label: 'I get more tired or fatigued more easily than usual.' }, { score: 2, label: 'I am too tired or fatigued to do a lot of the things I used to do.' }, { score: 3, label: 'I am too tired or fatigued to do most of the things I used to do.' }] },
        'bdi_21': { text: 'Loss of Interest in Sex', options: [{ score: 0, label: 'I have not noticed any recent change in my interest in sex.' }, { score: 1, label: 'I am less interested in sex than I used to be.' }, { score: 2, label: 'I am much less interested in sex now.' }, { score: 3, label: 'I have lost interest in sex completely.' }] },
    };

    const calculateBdi2Result = (resValue) => {
        
        if (typeof resValue !== 'object' || resValue === null) {
            return { score: 'N/A', severity: 'Could not be calculated.', numAnswered: 0 };
        }
        const scores = Object.values(resValue);
        const totalScore = scores.reduce((sum, score) => sum + (Number(score) || 0), 0);
        let severity = 'Minimal';
        if (totalScore >= 29) {
            severity = 'Severe';
        } else if (totalScore >= 20) {
            severity = 'Moderate';
        } else if (totalScore >= 14) {
            severity = 'Mild';
        }
        return { score: totalScore, severity: severity, numAnswered: scores.length };
    };

    const result = calculateBdi2Result(responseValue);
    const assessmentName = questionOptions.assessment_key || 'Standardized Assessment';

    return (
        <Box sx={{ mt: 1, p: 2, border: '1px solid', borderColor: 'primary.main', borderRadius: 2, background: 'primary.lightest' }}>
            <Typography variant="subtitle2" gutterBottom>Assessment Results: {assessmentName}</Typography>
            <Chip
                label={`Total Score: ${result.score}`}
                color="primary"
                variant="filled"
                sx={{ mr: 1, mb: 1 }}
            />
            <Chip
                label={`Severity: ${result.severity}`}
                color="secondary"
                variant="filled"
                sx={{ mr: 1, mb: 1 }}
            />
            <Chip
                label={`Questions Answered: ${result.numAnswered} / 21`}
                variant="outlined"
                size="small"
                 sx={{ mb: 1 }}
            />
            <Button size="small" onClick={() => setShowDetails(!showDetails)} sx={{ ml: 'auto', display: 'flex', mt: 1 }}>
                {showDetails ? 'Hide Details' : 'Show Details'}
            </Button>
            {showDetails && (
                
                <Box mt={2} pt={2} borderTop="1px solid #e0e0e0">
                    <Typography variant="subtitle2" gutterBottom>Individual Responses:</Typography>
                    <List dense disablePadding>
                        {Object.entries(responseValue || {}).map(([key, score]) => {
                            const questionData = BDI_II_QUESTIONS[key];
                            const responseOption = questionData?.options.find(opt => opt.score === score);
                            const itemNumber = key.split('_')[1] || key;
                            return (
                                <ListItem key={key} sx={{ py: 0.5, display: 'block' }}>
                                    <Typography variant="body2" component="div" sx={{ fontWeight: 'bold' }}>
                                        {`Item ${itemNumber}: ${questionData?.text || 'Unknown'}`}
                                    </Typography>
                                    <Typography variant="body2" component="div" sx={{ pl: 2, fontStyle: 'italic', color: 'text.secondary' }}>
                                        {`Score ${score}: ${responseOption?.label || 'Response not found'}`}
                                    </Typography>
                                </ListItem>
                            );
                        })}
                    </List>
                </Box>

            )}
        </Box>
    );
};

// --- START OF ADDITION (v2) ---
// New component to display feedback in a styled box
const FeedbackBox = ({ feedbackText, isModelAnswer = false }) => {
    if (!feedbackText) return null;
    return (
        <Paper
            variant="outlined"
            sx={{
                mt: 1.5,
                p: 1.5,
                backgroundColor: (theme) => alpha(theme.palette.success.main, 0.08),
                borderColor: 'success.light',
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1.5,
            }}
        >
            <LightbulbIcon sx={{ color: 'success.main', mt: 0.25 }} />
            <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'success.darker' }}>
                    {isModelAnswer ? 'Model Answer' : 'Explanation'}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                    {feedbackText}
                </Typography>
            </Box>
        </Paper>
    );
};
// --- END OF ADDITION (v2) ---

const ReadOnlySurveyRenderer = ({ surveyTitle, surveyDescription, blocks, patientResponses, contentItemType, sourceUrl, patientInfo }) => {
  if (contentItemType === 'education') {
    return (
        <Box sx={{ p: 2, mt: 1 }}>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>{surveyDescription}</Typography>
            {sourceUrl ? (
                <Alert severity="info" icon={<LinkIcon />} variant="outlined">
                    <Typography>
                        This resource links to an external website: <br />
                        <a href={sourceUrl.startsWith('http') ? sourceUrl : `https://${sourceUrl}`} target="_blank" rel="noopener noreferrer">{sourceUrl}</a>
                    </Typography>
                </Alert>
            ) : (
                <Alert severity="info" variant="outlined">This is a file-based educational resource (e.g., PDF) that will be sent to the patient's device.</Alert>
            )}
        </Box>
    );
  }

  if (!blocks || blocks.length === 0) {
    return <Typography sx={{p: 2}}>No survey content found to display.</Typography>;
  }

  // --- START OF EDIT 3 ---
  // Helper function to render media. We need this for the quiz blocks.
  const renderMedia = (questionOptions) => {
      const mediaAssets = Array.isArray(questionOptions.media_assets) ? questionOptions.media_assets : [];
      if (mediaAssets.length === 0) return null;

      const apiUrlBase = process.env.REACT_APP_API_BASE_URL || '';

      return (
          <Box sx={{ mb: 2, mt: 1 }}>
              <Grid container spacing={1}>
                  {mediaAssets.map((asset, index) => {
                      const fullAssetUrl = (asset.url && asset.url.startsWith('http')) 
                          ? asset.url 
                          : `${apiUrlBase.replace(/\/$/, '')}${asset.url}`;

                      return (
                          <Grid item xs={12} sm={6} key={asset.id || index}>
                              <Card variant="outlined">
                                  {asset.media_type === 'image' && asset.url && (
                                      <Box
                                          component="img"
                                          src={fullAssetUrl}
                                          alt={`Attachment ${index + 1}`}
                                          sx={{ width: '100%', height: 'auto', display: 'block' }}
                                      />
                                  )}
                                  {asset.media_type === 'video' && asset.embed_url && (
                                      <Box sx={{ position: 'relative', paddingBottom: '56.25%', height: 0, overflow: 'hidden' }}>
                                          <iframe
                                              src={asset.embed_url} // Embed URLs are usually absolute
                                              title={`Video Attachment ${index + 1}`}
                                              frameBorder="0"
                                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                              allowFullScreen
                                              style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                                          />
                                      </Box>
                                  )}
                              </Card>
                          </Grid>
                      );
                  })}
              </Grid>
          </Box>
      );
  };
  // --- END OF EDIT 3 ---

  const renderBlockInput = (block) => {
    if (!block || !block.id || !block.type) {
        console.error("ReadOnlySurveyRenderer: Attempting to render an invalid block:", block);
        return <Alert severity="error">Invalid question data encountered.</Alert>;
    }
    const blockId = block.id;
    let blockTypeStandard = typeof block.type === 'string' ? block.type.trim().toLowerCase() : '';

    let foundTypeKey = null;
    for (const key in BLOCK_TYPES) {
        if (BLOCK_TYPES[key].toLowerCase() === blockTypeStandard) {
            blockTypeStandard = BLOCK_TYPES[key];
            foundTypeKey = key;
            break;
        }
    }
     if (!foundTypeKey) { 
        console.warn(`ReadOnlySurveyRenderer: Block type "${block.type}" (standardized to "${blockTypeStandard}") for ID "${blockId}" does not match any known BLOCK_TYPES.`);
    }

    const questionOptions = parseOptions(block.options);
    const responseValue = patientResponses ? patientResponses[blockId] : undefined;
    
    // --- START OF ADDITION (v2) ---
    // Check if the patient has submitted an answer
    const hasResponded = responseValue !== undefined && responseValue !== null && responseValue !== '';
    // --- END OF ADDITION (v2) ---

    if (blockTypeStandard === BLOCK_TYPES.PAIN_MAP) {
        console.log(`[DEBUG] ReadOnlySurveyRenderer: Rendering PAIN_MAP block ${blockId}`);
        console.log(`[DEBUG] ReadOnlySurveyRenderer: Options:`, JSON.stringify(questionOptions, null, 2));
        console.log(`[DEBUG] ReadOnlySurveyRenderer: Response (points):`, JSON.stringify(responseValue, null, 2));
    }

    if (blockTypeStandard === BLOCK_TYPES.INFO_TEXT) {
        return ( <Typography variant="body1" sx={{ mt: 1, whiteSpace: 'pre-wrap', p:1, backgroundColor: 'transparent', borderRadius:1 }}>{block.question || block.prompt || '(No informational text provided)'}</Typography> );
    }
    else if (blockTypeStandard === BLOCK_TYPES.PAIN_MAP) {
        const painMapDisplayOptions = parseOptions(block.options);
        const patientMarkedPoints = Array.isArray(responseValue) ? responseValue : [];

        return (
            <Box sx={{ mt: 1, mb: 2 }}>
                {block.instruction_text && <Typography variant="body2" sx={{mb: 1}}>{block.instruction_text}</Typography>}
                <Box
                    sx={{
                        mt: 1,
                        position: 'relative',
                        width: '100%',
                        maxWidth: '500px', 
                        border: '1px solid #e0e0e0',
                        backgroundColor: '#fdfdfd',
                        borderRadius: '4px',
                        overflow: 'hidden', 
                        '& img': { display: 'block', width: '100%', height: 'auto' }
                    }}
                >
                    {painMapDisplayOptions.imageUrl ? (
                        <img src={process.env.PUBLIC_URL + painMapDisplayOptions.imageUrl} alt="Body diagram with pain points" />
                    ) : (
                        <Alert severity="warning">Body diagram image for this question is not available.</Alert>
                    )}
                    {patientMarkedPoints.map((point, idx) => (
                        <Tooltip
                            key={point.id || `painpoint-${idx}`}
                            title={`Pain Level: ${point.intensity !== undefined ? point.intensity : 'N/A'}${point.label ? ` - ${point.label}` : ''}`}
                            placement="top"
                        >
                            <Box
                                sx={{
                                    position: 'absolute',
                                    left: `${point.x}%`,
                                    top: `${point.y}%`,
                                    width: 12,
                                    height: 12,
                                    bgcolor: 'error.main',
                                    borderRadius: '50%',
                                    border: '2px solid white',
                                    transform: 'translate(-50%, -50%)',
                                    boxShadow: '0px 1px 4px rgba(0,0,0,0.6)'
                                }}
                            />
                        </Tooltip>
                    ))}
                </Box>
                {patientMarkedPoints.length === 0 && responseValue !== undefined && (
                    <Typography variant="caption" sx={{mt: 1, fontStyle: 'italic', color: 'text.secondary'}}>Patient indicated no specific pain points on the diagram.</Typography>
                )}
                 {responseValue === undefined && (
                    <Chip label="Not Answered" variant="outlined" size="small" icon={<HelpOutlineIcon />} sx={{ fontStyle: 'italic', mt: 1 }} />
                 )}
                 {patientMarkedPoints.length > 0 && (
                    <Box sx={{mt: 1.5}}>
                        <Typography variant="subtitle2" gutterBottom>Patient's Marked Pain Points:</Typography>
                        <List dense disablePadding>
                            {patientMarkedPoints.map((point, idx) => (
                                <ListItem key={point.id || `listpoint-${idx}`} disableGutters sx={{py: 0.25}}>
                                    <ListItemText 
                                        primary={
                                            <Typography variant="body2" component="span" sx={{display: 'flex', alignItems: 'center'}}>
                                                <CircleIcon sx={{ fontSize: '0.8rem', color: 'error.main', mr: 0.75 }} />
                                                Point {idx + 1}: Intensity <strong>{point.intensity !== undefined ? point.intensity : 'N/A'}</strong>
                                                {point.label && <Typography component="span" variant="body2" sx={{ml:0.5}}>(Label: {point.label})</Typography>}
                                            </Typography>
                                        } 
                                    />
                                </ListItem>
                            ))}
                        </List>
                    </Box>
                )}
            </Box>
        );
    }

    // --- START OF EDIT 4 ---
    // Combine OPEN_TEXT and QUIZ_QUESTION_OPEN_TEXT
    else if (blockTypeStandard === BLOCK_TYPES.OPEN_TEXT || blockTypeStandard === BLOCK_TYPES.QUIZ_QUESTION_OPEN_TEXT) {
    // --- END OF EDIT 4 ---
    
        // This block was incorrect anyway. Re-using media logic from SurveyRenderer.
        const mediaAssets = Array.isArray(questionOptions.media_assets) ? questionOptions.media_assets : [];
        const apiUrlBase = process.env.REACT_APP_API_BASE_URL || '';

        return (
            <>
                {/* Use the new renderMedia helper function */}
                {renderMedia(questionOptions)}
                <Box sx={{ mt: 1.5, pl: 0 }}>
                    <FormLabel component="legend" sx={{ typography: 'body2', mb: 1, color: 'text.secondary' }}>Patient's Response:</FormLabel>
                    {renderFormattedResponseValue(block, responseValue, questionOptions)}
                    
                    {/* --- START OF ADDITION (v2) --- */}
                    {/* Show model answer if patient has responded */}
                    {hasResponded && (
                        <FeedbackBox
                            feedbackText={questionOptions.model_answer}
                            isModelAnswer={true}
                        />
                    )}
                    {/* --- END OF ADDITION (v2) --- */}
                </Box>
            </>
        );
    }
    
    // --- START OF EDIT 5 ---
    // ADD NEW `else if` block for QUIZ_QUESTION_CHOICE
    else if (blockTypeStandard === BLOCK_TYPES.QUIZ_QUESTION_CHOICE) {
        
        // --- START OF ADDITION (v2) ---
        // Find the specific feedback for the *chosen* option, if it exists
        let perChoiceFeedback = null;
        if (hasResponded && Array.isArray(questionOptions.choices)) {
            const selectedOption = questionOptions.choices.find(opt => String(opt.value) === String(responseValue));
            if (selectedOption && selectedOption.feedback) {
                perChoiceFeedback = selectedOption.feedback;
            }
        }
        // --- END OF ADDITION (v2) ---

        return (
            <>
                {/* Use the new renderMedia helper function */}
                {renderMedia(questionOptions)}
                <Box sx={{ mt: 1.5, pl: 0 }}>
                     {renderFormattedResponseValue(block, responseValue, questionOptions)}
                     
                    {/* --- START OF ADDITION (v2) --- */}
                    {/* Show feedback if patient has responded */}
                    {hasResponded && (
                        <FeedbackBox
                            // Prioritize the new top-level feedback, fall back to per-choice
                            feedbackText={questionOptions.question_feedback || perChoiceFeedback}
                            isModelAnswer={false} // It's an explanation
                        />
                    )}
                    {/* --- END OF ADDITION (v2) --- */}
                </Box>
            </>
        );
    }
    // --- END OF EDIT 5 ---
    
    else {
        return (
            <Box sx={{ mt: 1.5, pl: 0 }}>
                 {renderFormattedResponseValue(block, responseValue, questionOptions)}
            </Box>
        );
    }
  };

  return (
    <Box>
        {patientInfo && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                Patient: {patientInfo.first_name} {patientInfo.last_name} (DOB: {patientInfo.patient_profile?.date_of_birth ? format(parseISO(patientInfo.patient_profile.date_of_birth), 'P') : 'N/A'})
            </Typography>
        )}
        {blocks.map((block, index) => (
            <Paper elevation={0} sx={{ mb: 3, p: 2, borderRadius: '4px', border: '1px solid #eee' }} key={block.id || `block-idx-${index}`}>
                <FormLabel component="legend" sx={{ typography: 'subtitle1', mb: 1, fontWeight: 'medium', color: 'text.primary', display: 'block' }}>
                    {`Q${index + 1}: ${block.prompt || block.question || `Question ${index + 1}`}`}
                </FormLabel>
                {renderBlockInput(block)}
            </Paper>
        ))}
         {blocks.length === 0 && <Typography sx={{p: 2, textAlign:'center', color:'text.secondary'}}>No questions were part of this assignment's content.</Typography>}
         {blocks.length > 0 && Object.keys(patientResponses || {}).length === 0 && (
             <Alert severity="info" sx={{mt: 2}}>No responses have been recorded for this assignment yet.</Alert>
         )}
    </Box>
  );
};

export default ReadOnlySurveyRenderer;